/* eslint-disable prefer-template */
import React, { createContext, useCallback, useState, useContext } from "react";
import Swal from "sweetalert2";

import { useNavigate } from "react-router-dom";
import {
  ref,
  push as firebasePush,
  set,
  get,
  child,
  remove,
} from "firebase/database";
import { database } from "../config/firebase";
import { atividadeProps } from "../pages/AtvForm";

interface ModuloProps {
  title: string;
  tumblr?: string;
  description: string;
}
export interface DataTypeModule {
  key: "uid";
  uid: string;
  tumblr: string;
  title: string;
  description: string;
  dados: string;
}
export interface DataTypeVideo {
  key: "uid";
  uid: string;
  tumblr: string;
  title: string;
  link: string;
}

export interface DataTypeActivity {
  uid: string;
  multipleChoice: atividadeProps[];
}

interface ModuloContextData {
  Modulos: ModuloProps[];
  loadingModulos: boolean;
  createModulos(ModulosData: ModuloProps): Promise<void>;
  getModules(): Promise<DataTypeModule[]>;
  getVideos(uid: string): Promise<DataTypeVideo[]>;
  getActivities(uid: string): Promise<DataTypeActivity[]>;
  removeModule(uid: string): Promise<void>;
}

const ModuloContext = createContext<ModuloContextData>({} as ModuloContextData);

export async function createAtividade(
  multipleChoice: atividadeProps[],
  uidModulo: string
): Promise<string | null> {
  if (!uidModulo || !multipleChoice.length) {
    throw new Error("Módulo ou questões inválidos");
  }

  const activityRef = firebasePush(
    child(ref(database), `modules/${uidModulo}/activities`)
  );
  const uid = activityRef.key;
  await set(activityRef, {
    multipleChoice,
    uid,
  });
  return uid;
}

const ModulosProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [ModulosData] = useState([] as ModuloProps[]);
  const [loadingModulos, setLoadingModulos] = useState(false);

  const getVideos = useCallback(async (uid: string) => {
    const videos = [] as DataTypeVideo[];
    try {
      const snapshot = await get(child(ref(database), `modules/${uid}/videos`));

      snapshot.forEach((current) => {
        videos.push(current.val());
      });
      return videos;
    } catch (error) {
      throw new Error("Houve um erro ao Carregar os Videos");
    }
  }, []);

  const getActivities = useCallback(async (uid: string) => {
    const activities = [] as DataTypeActivity[];
    try {
      const snapshot = await get(
        child(ref(database), `modules/${uid}/activities`)
      );
      if (!snapshot.exists()) return activities;
      snapshot.forEach((current) => {
        const val = current.val();
        if (val) {
          activities.push({
            uid: val.uid || current.key || "",
            multipleChoice: Array.isArray(val.multipleChoice)
              ? val.multipleChoice
              : [],
          });
        }
      });
      return activities;
    } catch (error) {
      throw new Error("Houve um erro ao carregar as atividades");
    }
  }, []);

  const getModules = useCallback(async () => {
    try {
      const res = await get(child(ref(database), "/modules"));
      const modulesList = [] as DataTypeModule[];
      res.forEach((current) => {
        modulesList.push(current.val());
      });
      console.log(res.val());
      return modulesList;
    } catch (error) {
      throw new Error("Houve um erro ao Carregar as Turmas");
    }
  }, []);

  const removeModule = useCallback(async (uid: string) => {
    // pra apagar nas turmas
    const snapshot = await get(child(ref(database), "classes"));
    const classes: any[] = [];
    snapshot.forEach((currentSnapShot) => {
      classes.push(currentSnapShot.val());
    });

    await Promise.all(
      classes.map((current) =>
        remove(child(ref(database), `classes/${current.uid}/modules/${uid}`))
      )
    );
    // pra apagar na student class

    const studentsClassesSnapshot = await get(
      child(ref(database), "students_classes")
    );
    const studentClasses: any[] = [];

    studentsClassesSnapshot.forEach((currentSnapShot) => {
      studentClasses.push(currentSnapShot.val());
    });

    const deletePromises: any[] = [];
    studentClasses.forEach((currentStudentClasse) => {
      classes.forEach((currentClass) => {
        deletePromises.push(
          remove(
            child(
              ref(database),
              `students_classes/${currentStudentClasse.uid}/${currentClass.uid}/modules/${uid}`
            )
          )
        );
      });
    });

    await Promise.all(deletePromises);

    await remove(child(ref(database), `modules/${uid}`));
  }, []);

  const createModulos = useCallback(
    async ({ title, tumblr, description }: ModuloProps) => {
      setLoadingModulos(true);

      Swal.fire({
        title: "Cadastrando...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          const moduleRef = firebasePush(child(ref(database), "modules"));
          const uid = moduleRef.key;
          Swal.showLoading();
          try {
            await set(moduleRef, {
              title,
              tumblr,
              description,
              uid,
            });
            Swal.close();
            await Swal.fire({
              icon: "success",
              title: "Cadastro de Módulo",
              text: "Módulo cadastrado com sucesso!",
              showConfirmButton: false,
              timer: 1800,
            });
            navigate("/modules");
          } catch (err) {
            console.log(err);
            Swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.",
            });
          } finally {
            setLoadingModulos(false);
          }
        },
      });
    },
    [navigate]
  );
  const providerValue = React.useMemo(
    () => ({
      getVideos,
      getActivities,
      removeModule,
      getModules,
      createModulos,
      Modulos: ModulosData,
      loadingModulos,
    }),
    [
      getVideos,
      getActivities,
      removeModule,
      getModules,
      createModulos,
      ModulosData,
      loadingModulos,
    ]
  );

  return (
    <ModuloContext.Provider value={providerValue}>
      {children}
    </ModuloContext.Provider>
  );
};

function useModulos(): ModuloContextData {
  const context = useContext(ModuloContext);

  if (!context) {
    throw new Error("useModulos must be used within an AuthProvider");
  }

  return context;
}

export { ModulosProvider, useModulos };
